import React, { useState } from 'react';
import { ArrowLeft, LineChart, BarChart3, PieChart, Wallet, Home } from 'lucide-react';
import AdvancedCharts from '../widgets/AdvancedCharts/AdvancedCharts';
import { Token } from '../types/supabase';

interface AdvancedChartsPageProps {
  onGoBack: () => void;
  onGoHome: () => void;
  onNavigateToModule: (route: string) => void;
  walletAddress?: string;
  walletProvider?: 'phantom' | 'solflare';
  onShowConnectModal?: () => void;
  onDisconnectWallet?: () => void;
}

const AdvancedChartsPage: React.FC<AdvancedChartsPageProps> = ({ 
  onGoBack, 
  onGoHome, 
  onNavigateToModule,
  walletAddress, 
  walletProvider, 
  onShowConnectModal, 
  onDisconnectWallet 
}) => {
  const [selectedToken, setSelectedToken] = useState<Token | undefined>();
  const [activeModule, setActiveModule] = useState('charts-page');

  const modules = [
    {
      id: 'portfolio-page',
      name: 'Portfolio',
      icon: <PieChart className="w-4 h-4" />,
      route: 'portfolio-page'
    },
    {
      id: 'wallets-page',
      name: 'Wallets',
      icon: <Wallet className="w-4 h-4" />,
      route: 'wallets-page'
    },
    {
      id: 'charts-page',
      name: 'Charts',
      icon: <BarChart3 className="w-4 h-4" />,
      route: 'charts-page'
    }
  ];

  const handleModuleClick = (module: typeof modules[0]) => {
    if (module.route !== activeModule) {
      onNavigateToModule(module.route);
    }
  };

  return (
    <div className="fixed inset-0 bg-white flex flex-col overflow-hidden">
      {/* Module Navigation Bar - Flush below navbar */}
      <div className="absolute top-14 left-0 right-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm z-40">
        <div className="flex items-center gap-6">
          {/* Back to Modules */}
          <button
            onClick={onGoBack}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Modules</span>
          </button>

          {/* Module Navigation Tabs */}
          <div className="flex items-center gap-1">
            {modules.map((module) => (
              <button
                key={module.id}
                onClick={() => handleModuleClick(module)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeModule === module.route
                    ? 'bg-[#24c35b] text-white shadow-sm'
                    : 'text-gray-600 hover:text-black hover:bg-gray-50'
                }`}
              >
                {module.icon}
                <span>{module.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Right Side - Wallet Status & Home */}
        <div className="flex items-center gap-4">
          {/* Wallet Connection Status */}
          {walletAddress ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#24c35b]/10 rounded-lg border border-[#24c35b]/20">
              <div className="w-2 h-2 rounded-full bg-[#24c35b] animate-pulse"></div>
              <span className="text-sm font-medium text-[#24c35b]">
                {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
              </span>
            </div>
          ) : (
            <button
              onClick={onShowConnectModal}
              className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Connect Wallet
            </button>
          )}

          {/* Home Button */}
          <button
            onClick={onGoHome}
            className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
          >
            <Home className="w-4 h-4" />
            <span className="font-medium">Home</span>
          </button>
        </div>
      </div>

      {/* Main Content Area - Full Viewport below navigation */}
      <div className="absolute top-[110px] left-0 right-0 bottom-0 bg-white overflow-hidden">
        {/* Advanced Charts Container - Full Screen with style overrides */}
        <div 
          className="w-full h-full relative"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%'
          }}
        >
          <style>{`
            /* Override widget container styling for full-screen mode */
            .advanced-charts-fullscreen .flex.flex-col.bg-white.h-full.w-full.rounded-xl {
              border-radius: 0 !important;
              border: none !important;
              box-shadow: none !important;
              height: 100% !important;
              width: 100% !important;
            }
            
            /* Ensure chart container takes full space */
            .advanced-charts-fullscreen [data-widget-type="advanced-charts"] {
              height: 100% !important;
              width: 100% !important;
              border-radius: 0 !important;
              border: none !important;
            }
            
            /* Override any padding/margins */
            .advanced-charts-fullscreen .chart-container-wrapper,
            .advanced-charts-fullscreen .chart-container {
              height: 100% !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          `}</style>
          <div className="advanced-charts-fullscreen w-full h-full">
            <AdvancedCharts 
              selectedToken={selectedToken}
              isWidget={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedChartsPage; 